/**
 * Default preview fallback — when a retrieved item has NO real preview image
 * (text articles never do; a video whose URL yields no YouTube thumbnail),
 * pick an intuitive on-brand static so the hero is never an empty gradient.
 *
 * The choice is driven by the corpus we already know — the item's media_kind
 * (derived) + its category tags — routed to one of three pools:
 *   - meditation → calm practice photos      (meditation / breathwork / stress)
 *   - wisdom     → a Gurudev portrait         (talks, quotes, knowledge, satsang)
 *   - community  → warm lifestyle photos      (everything else / general)
 *
 * Within a pool the pick is a DETERMINISTIC rotation keyed on the item's URL,
 * so the same item always shows the same image (no flicker across re-renders /
 * streaming) while different items rotate through the set.
 */

import type { RAGReference } from '@/types/rag';
import { deriveMediaKind } from '@/components/assistant/thinking/media-kind';

export type DefaultCategory = 'meditation' | 'wisdom' | 'community';

// Under /images/ on purpose: the QA/prod edge only forwards known path
// prefixes to this app (/us-en, /images, /_next, …) — a top-level
// /defaults/* 404s at the edge even though the files ship in the build
// (FB27 #4: every article card showed a broken-image glyph on QA).
const POOLS: Record<DefaultCategory, string[]> = {
  meditation: [
    '/images/defaults/meditation-1.jpg',
    '/images/defaults/meditation-2.jpg',
  ],
  wisdom: ['/images/defaults/gurudev-1.png'],
  community: [
    '/images/defaults/community-1.jpg',
    '/images/defaults/community-2.jpg',
    '/images/defaults/community-3.jpg',
    '/images/defaults/community-4.jpg',
  ],
};

const MEDITATION_RE =
  /meditat|breath|calm|sleep|anxiet|stress|relax|kriya|\bsky\b/i;
const WISDOM_RE =
  /wisdom|gurudev|guru|knowledge|satsang|spiritual|talk|quote|bhajan/i;

export function defaultCategoryFor(ref: RAGReference): DefaultCategory {
  const mk = deriveMediaKind(ref);
  const cat = ref.category ?? '';
  if (mk === 'meditation' || MEDITATION_RE.test(cat)) return 'meditation';
  if (mk === 'quote' || mk === 'talk' || WISDOM_RE.test(cat)) return 'wisdom';
  return 'community';
}

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++)
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export interface DefaultPreview {
  src: string;
  category: DefaultCategory;
  /**
   * wisdom = a transparent Gurudev portrait → render `contain` on a warm
   * gradient; the lifestyle photos are full-bleed → `cover`.
   */
  fit: 'cover' | 'contain';
}

export function defaultPreview(ref: RAGReference): DefaultPreview {
  const category = defaultCategoryFor(ref);
  const pool = POOLS[category];
  const src = pool[hash(ref.url || ref.title || '') % pool.length];
  return { src, category, fit: category === 'wisdom' ? 'contain' : 'cover' };
}
